import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import AdminService, { PlatformStats } from '../../services/admin/admin.service';

export const DashboardPage: React.FC = () => {
  const { accessToken } = useAuth();
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    if (!accessToken) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await AdminService.getPlatformStats(accessToken);
      setStats(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load stats');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div style={styles.container}>
        <h1 style={styles.title}>Platform Dashboard</h1>
        <p style={styles.loading}>Loading statistics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <h1 style={styles.title}>Platform Dashboard</h1>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Platform Dashboard</h1>
        <button onClick={fetchStats} style={styles.refreshButton}>
          🔄 Refresh
        </button>
      </div>

      {/* User Stats */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>User Statistics</h2>
        <div style={styles.grid}>
          <div style={styles.card}>
            <div style={styles.cardIcon}>👥</div>
            <div style={styles.cardContent}>
              <div style={styles.cardValue}>{stats.users.total.toLocaleString()}</div>
              <div style={styles.cardLabel}>Total Users</div>
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.cardIcon}>✅</div>
            <div style={styles.cardContent}>
              <div style={styles.cardValue}>{stats.users.active.toLocaleString()}</div>
              <div style={styles.cardLabel}>Active Users (7d)</div>
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.cardIcon}>⭐</div>
            <div style={styles.cardContent}>
              <div style={styles.cardValue}>{stats.users.premium.toLocaleString()}</div>
              <div style={styles.cardLabel}>Premium Users</div>
            </div>
          </div>

          <div style={{ ...styles.card, ...styles.cardDanger }}>
            <div style={styles.cardIcon}>🚫</div>
            <div style={styles.cardContent}>
              <div style={styles.cardValue}>{stats.users.banned.toLocaleString()}</div>
              <div style={styles.cardLabel}>Banned Users</div>
              <div style={styles.cardSubtext}>Ban Rate: {stats.users.banRate}%</div>
            </div>
          </div>
        </div>
      </div>

      {/* Session Stats */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Session Statistics</h2>
        <div style={styles.grid}>
          <div style={styles.card}>
            <div style={styles.cardIcon}>📊</div>
            <div style={styles.cardContent}>
              <div style={styles.cardValue}>{stats.sessions.total.toLocaleString()}</div>
              <div style={styles.cardLabel}>Total Sessions</div>
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.cardIcon}>📅</div>
            <div style={styles.cardContent}>
              <div style={styles.cardValue}>{stats.sessions.today.toLocaleString()}</div>
              <div style={styles.cardLabel}>Sessions Today</div>
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.cardIcon}>📈</div>
            <div style={styles.cardContent}>
              <div style={styles.cardValue}>{stats.sessions.thisWeek.toLocaleString()}</div>
              <div style={styles.cardLabel}>Sessions This Week</div>
            </div>
          </div>
        </div>
      </div>

      {/* Report Stats */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Report Statistics</h2>
        <div style={styles.grid}>
          <div style={styles.card}>
            <div style={styles.cardIcon}>🚨</div>
            <div style={styles.cardContent}>
              <div style={styles.cardValue}>{stats.reports.totalReports.toLocaleString()}</div>
              <div style={styles.cardLabel}>Total Reports</div>
            </div>
          </div>

          <div style={{ ...styles.card, ...styles.cardWarning }}>
            <div style={styles.cardIcon}>⚠️</div>
            <div style={styles.cardContent}>
              <div style={styles.cardValue}>{stats.reports.unreviewedReports.toLocaleString()}</div>
              <div style={styles.cardLabel}>Unreviewed Reports</div>
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.cardIcon}>🕐</div>
            <div style={styles.cardContent}>
              <div style={styles.cardValue}>{stats.reports.reportsLast24h.toLocaleString()}</div>
              <div style={styles.cardLabel}>Reports (24h)</div>
            </div>
          </div>
        </div>
      </div>

      {/* Admin Action Stats */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Moderation Actions</h2>
        <div style={styles.grid}>
          <div style={styles.card}>
            <div style={styles.cardIcon}>⚡</div>
            <div style={styles.cardContent}>
              <div style={styles.cardValue}>{stats.adminActions.total.toLocaleString()}</div>
              <div style={styles.cardLabel}>Total Actions</div>
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.cardIcon}>🤖</div>
            <div style={styles.cardContent}>
              <div style={styles.cardValue}>{stats.adminActions.autoBans.toLocaleString()}</div>
              <div style={styles.cardLabel}>Auto-Bans</div>
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.cardIcon}>👨‍💼</div>
            <div style={styles.cardContent}>
              <div style={styles.cardValue}>{stats.adminActions.manualBans.toLocaleString()}</div>
              <div style={styles.cardLabel}>Manual Bans</div>
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.cardIcon}>📅</div>
            <div style={styles.cardContent}>
              <div style={styles.cardValue}>{stats.adminActions.today.toLocaleString()}</div>
              <div style={styles.cardLabel}>Actions Today</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    maxWidth: '1400px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
  },
  title: {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0,
  },
  refreshButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  loading: {
    fontSize: '1rem',
    color: '#6b7280',
  },
  error: {
    padding: '1rem',
    backgroundColor: '#fee',
    color: '#c33',
    borderRadius: 'var(--radius-md)',
  },
  section: {
    marginBottom: '2.5rem',
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '1rem',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1.5rem',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: 'var(--radius-lg)',
    padding: '1.5rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    transition: 'all 0.2s ease',
  },
  cardWarning: {
    backgroundColor: '#fef3c7',
    borderLeft: '4px solid #f59e0b',
  },
  cardDanger: {
    backgroundColor: '#fee2e2',
    borderLeft: '4px solid #dc2626',
  },
  cardIcon: {
    fontSize: '2.5rem',
  },
  cardContent: {
    flex: 1,
  },
  cardValue: {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '0.25rem',
  },
  cardLabel: {
    fontSize: '0.9rem',
    color: '#6b7280',
    fontWeight: '500',
  },
  cardSubtext: {
    fontSize: '0.85rem',
    color: '#9ca3af',
    marginTop: '0.25rem',
  },
};
